import type { ProjectView, WikiPendingSet, WikiReviewQueue } from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { GitPullRequestArrow } from "lucide-react";
import { createContext, type ReactNode, useContext, useEffect, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { can } from "../projects/capabilities.ts";
import { changeSetPath, Link } from "../router.tsx";
import { useSession } from "../session.tsx";
import { useReviewQueue, wikiKeys } from "./queries.ts";
import { useReviewPref } from "./review-prefs.ts";

// Pending-change hints on the live wiki (wiki-collaboration-ux D9): a Pending changes switch in
// the wiki's toolbar (Shift+P), on by default for a viewer who may decide in the project and
// remembered per person in the browser; when on, a page touched by a submitted open set the
// viewer may see opens with one line naming them and a bar beside each section an edit changes.
// Everything comes from the server's filtered answer: a set the viewer may not see adds nothing.

type Project = Pick<ProjectView, "capabilities" | "frozenAt"> & { id: string };

/** Whether hints show, and the switch, for the wiki's toolbar and its page. */
export interface PendingHints {
  on: boolean;
  set: (on: boolean) => void;
  available: boolean;
}
export const PendingHintsContext = createContext<PendingHints | null>(null);

/** The person's switch: their choice, else on for a viewer who may decide (publish). */
export function usePendingHintsState(project: Project): PendingHints {
  const { userId } = useSession();
  const [pref, setPref] = useReviewPref("pending", userId, ["on", "off"] as const);
  const available = can(project, "wiki.view_drafts");
  const on = available && (pref ? pref === "on" : can(project, "wiki.publish"));
  return { on, set: (next) => setPref(next ? "on" : "off"), available };
}

/** `Shift+P` toggles the hints while the page has focus, outside fields and dialogs. */
export function usePendingShortcut(hints: PendingHints, active: boolean) {
  useEffect(() => {
    if (!active || !hints.available) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (
        e.key.toLowerCase() !== "p" ||
        !e.shiftKey ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        target?.closest("input, textarea, select, [contenteditable='true'], [role='dialog']")
      ) {
        return;
      }
      // Captured before the Pages tree, whose rows would read the letter as type-ahead.
      e.preventDefault();
      e.stopPropagation();
      hints.set(!hints.on);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [active, hints]);
}

/** The toolbar's switch (labelled, its shortcut in the name's description). */
export function PendingSwitch(props: { hints: PendingHints }) {
  const { t } = useTranslation();
  const id = useId();
  if (!props.hints.available) {
    return null;
  }
  return (
    <div className="flex items-center gap-2 px-2">
      <Switch
        id={id}
        checked={props.hints.on}
        aria-keyshortcuts="Shift+P"
        onCheckedChange={(on) => props.hints.set(on)}
      />
      <Label htmlFor={id} className="text-[13px] font-normal text-fg-secondary">
        {t("wiki.pending.toggle")}
        <kbd className="ml-1.5 hidden rounded border border-line px-1 font-sans text-[11px] text-fg-muted sm:inline">
          {t("wiki.pending.shortcut")}
        </kbd>
      </Label>
    </div>
  );
}

/** Kinds of item that touch a published page, and the undecided statuses (`pendingForPage`). */
const TOUCHING_OPS = new Set(["edit", "rename", "move", "delete"]);
const UNDECIDED = new Set(["pending", "approved", "stale"]);

/**
 * The pages (lowercased IDs) a submitted open set touches, from the review queue: the server
 * lists only the items the viewer may see, so a hidden set or item marks nothing. A working set
 * (not submitted) touches nothing; an N-1 API without `submittedAt` lists submitted sets only.
 */
export function pendingPageIds(queue: WikiReviewQueue): Set<string> {
  const ids = new Set<string>();
  for (const set of queue.changeSets) {
    if (set.submittedAt === null || (set.status !== "open" && set.status !== "changes_requested")) {
      continue;
    }
    for (const item of set.items) {
      if (item.pageId && TOUCHING_OPS.has(item.op) && UNDECIDED.has(item.status)) {
        ids.add(item.pageId.toLowerCase());
      }
    }
  }
  return ids;
}

/**
 * The pages to mark in the Pages panel (#411): only inside the wiki's Pages (the switch's
 * context), while the switch is on; empty otherwise. Shares the review queue's read.
 */
export function usePendingPages(projectId: string, enabled: boolean): ReadonlySet<string> {
  const hints = useContext(PendingHintsContext);
  const on = enabled && !!hints?.on;
  const queue = useReviewQueue(projectId, on);
  return useMemo(() => (on && queue.data ? pendingPageIds(queue.data) : EMPTY), [on, queue.data]);
}
const EMPTY: ReadonlySet<string> = new Set();

/** The sets touching a page, as the viewer may see them; only while hints are on. */
export function usePendingForPage(projectId: string, pageId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...wikiKeys.all(projectId), "pending", pageId],
    enabled,
    retry: false,
    queryFn: async (): Promise<WikiPendingSet[]> =>
      (
        await ok(
          await api.api.projects[":projectId"].wiki.pages[":pageId"].pending.$get({
            param: { projectId, pageId },
          }),
        )
      ).sets,
  });
}

/**
 * The page's hints: the banner, and the sections to mark. Nothing on an older version, when the
 * switch is off, on an N-1 API (no route), or when no visible set touches the page.
 */
export function usePageHints(project: Project, pageId: string, current: boolean) {
  const hints = useContext(PendingHintsContext);
  const on = !!hints?.on && current;
  const pending = usePendingForPage(project.id, pageId, on);
  const sets = on && !pending.isError ? (pending.data ?? []) : [];
  const sections = new Set(sets.flatMap((s) => s.items.flatMap((i) => i.sections)));
  return {
    banner: sets.length ? <PendingBanner projectId={project.id} sets={sets} /> : null,
    bar: (slug: string): ReactNode => (sections.has(slug) ? <SectionBar /> : null),
  };
}

function PendingBanner(props: { projectId: string; sets: readonly WikiPendingSet[] }) {
  const { t } = useTranslation();
  const count = props.sets.reduce((n, s) => n + s.items.length, 0);
  const single = props.sets.length === 1 ? props.sets[0] : null;
  return (
    <p
      data-pending-banner=""
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-warning-border/45 bg-warning-bg px-3 py-2 text-[13px] text-warning-fg"
    >
      <GitPullRequestArrow aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.75} />
      <span>{t("wiki.pending.banner", { count })}</span>
      {single ? (
        <>
          <span aria-hidden="true">·</span>
          <Link
            href={changeSetPath(props.projectId, single.id, single.items[0]?.id)}
            aria-label={
              single.title
                ? t("wiki.pending.viewSet", { title: single.title })
                : t("wiki.pending.viewUnnamed")
            }
            className="font-medium underline underline-offset-4"
          >
            {t("wiki.pending.view")}
          </Link>
        </>
      ) : (
        props.sets.map((s) => (
          <span key={s.id} className="flex items-center gap-2">
            <span aria-hidden="true">·</span>
            <Link
              href={changeSetPath(props.projectId, s.id, s.items[0]?.id)}
              data-esi-content="wiki"
              className="font-medium underline underline-offset-4"
            >
              {s.title ?? t("wiki.pending.unnamed")}
            </Link>
          </span>
        ))
      )}
    </p>
  );
}

/** A bar in the margin beside a section's heading; its words for assistive technology. */
function SectionBar() {
  const { t } = useTranslation();
  return (
    <span data-pending-bar="">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0.5 -left-3 w-[3px] rounded-full bg-warning-border sm:-left-4"
      />
      <span className="sr-only">{t("wiki.pending.section")}</span>
    </span>
  );
}
